<script setup lang="ts">
import type { CmsElementBioCircle } from "~/composables/useCustom";
import { getProductsById } from "~/server/customAPI";
import { buildUrlPrefix, getProductRoute } from "@shopware/helpers";
import { useUrlResolver } from "#imports";
import { combineProductProperties } from "~/components/utilities";
import SharedHeadline from "~/components/shared/SharedHeadline.vue";
import SharedTopline from "~/components/shared/SharedTopline.vue";
import { Carousel, Slide, Navigation } from "vue3-carousel";
const { getUrlPrefix } = useUrlResolver();

const props = defineProps<{
  content: CmsElementBioCircle;
}>();

const { headlineText, toplineText, toplineType, headlineType, Boxes } =
  props.content?.data;

let ProductIds: any = [];

if (Boxes && Boxes.length) {
  Boxes.forEach((product: any) => {
    ProductIds.push(product.boxProduct);
  });
}

const searchResults = ref<any>({ elements: [] });
searchResults.value = await getProductsById({
  ids: ProductIds,
  associations: {
    manufacturer: {
      associations: {
        media: {},
      },
    },
    crossSellings: {
      products: {},
    },
    properties: {
      associations: {
        group: {},
      },
    },
    options: {},
    seoUrls: {},
    cover: {},
  },
});

const productSlider = ref(null);
const currentSlide = ref(0);

const slideTo = (val: any) => {
  currentSlide.value = val;
};
let properties: any = [];

if (searchResults.value.elements && searchResults.value.elements.length) {
  properties = combineProductProperties(
    searchResults.value.elements[0].properties
  );

  searchResults.value.elements.forEach((product: any) => {
    product.combindedProps = combineProductProperties(product.properties);
  });
}

const settings = {
  itemsToShow: 3,
  itemsToScroll: 3,
  wrapAround: false,
  snapAlign: "start" as "start",
  autoplay: 0,
  transition: 600,
  pauseAutoplayOnHover: true,
  breakpoints: {
    // 100px and up
    100: {
      itemsToShow: 1,
      itemsToScroll: 1,
      snapAlign: "center" as "center",
    },
    400: {
      itemsToShow: 1,
      itemsToScroll: 1,
      snapAlign: "start",
    },
    1000: {
      itemsToShow: 3,
      itemsToScroll: 3,
      snapAlign: "start",
    },
  },
};


const itemsToShow = computed(() => {
  return productSlider?.value?.data?.config?.itemsToShow;
});

const showNavigation = computed(() => {
  return itemsToShow.value < searchResults.value.elements.length;
});

</script>
<template>
  <section>
    <div class="mb-10">
      <div class="container px-0 xl:px-5">
        <SharedTopline
          :headline="toplineText"
          :headlineType="toplineType"
        ></SharedTopline>
        <SharedHeadline
          :headline="headlineText"
          :headlineType="headlineType"
        ></SharedHeadline>
      </div>
    </div>
    <div class="relative" v-if="Boxes && searchResults && searchResults.elements.length > 0">
      <div class="container px-0 xl:px-5">
        <div class="flex">
          <div class="w-1/4 hidden md:block relative z-[1] pointer-events-none">
            <span class="absolute z-[1] top-0 -left-[500px] right-[0px] w-auto h-full bg-skin-grey-light"></span>
            <div class="parameters relative z-[2]">
              <figure class="w-[12.75rem] h-[12.75rem] mx-auto mb-5"></figure>
              <h4 class="font-bold text-center text-base h-14">&nbsp;</h4>
              <ul
                class="text-left prose-li:py-3 [&>li:not(:last-child)]:border-b prose-li:border-skin-primary uppercase font-bold text-skin-secondary text-sm"
              >
                <template
                  v-if="properties"
                  v-for="(property, key) in properties"
                  :key="key"
                >
                  <li>
                    {{ key }}
                  </li>
                </template>
              </ul>
            </div>
          </div>
          <div
            v-if="Boxes"
            id="CompareProductsCarousel"
            class="w-full md:w-3/4 [&>.slick-arrow]:top-[20%]"
          >
            <Carousel
              v-bind="settings"
              ref="productSlider"
              v-model="currentSlide"
              class="!relative lg:!static"
            >
              <template #slides>
                <Slide
                  v-for="product of searchResults.elements"
                  :key="product.id"
                >
                  <div class="group relative z-[1] slide text-center w-[100vw] md:w-auto overflow-hidden">
                    <figure
                      class="w-[12.75rem] h-[12.75rem] mx-auto mb-5 flex items-center justify-center relative aspect-square"
                    >
                      <NuxtImg
                        v-if="product.cover"
                        loading="lazy"
                        format="webp"
                        width="250"
                        class="object-contain w-full h-full"
                        :src="product.cover.media.url"
                        alt="product.translated.name"
                      />
                    </figure>
                    <h4 class="font-bold text-center text-base h-14">
                      {{ product.translated.name }}
                    </h4>
                    <ul
                      class="text-left md:text-center prose-li:py-3 [&>li:not(:last-child)]:border-b prose-li:border-skin-primary text-sm mobile:prose-li:flex mobile:prose-li:justify-between mobile:prose-li:pr-4 mobile:prose-li:flex-wrap"
                    >
                      <template
                        v-if="properties"
                        v-for="(property, key) in properties"
                        :key="key"
                      >
                        <li style="">
                          <span
                            class="md:hidden text-skin-secondary uppercase font-bold"
                            >{{ key }}:</span
                          >
                          <span>
                            {{ product.combindedProps[key] || "-" }}
                          </span>
                        </li>
                      </template>
                    </ul>
                    <div class="mt-1">
                      <NuxtLink
                        class="btn-outline !py-[0.5rem]"
                        :to="
                          buildUrlPrefix(
                            getProductRoute(product),
                            getUrlPrefix()
                          )
                        "
                      >
                        {{ $t("learnMore") }}
                      </NuxtLink>
                    </div>
                  </div>
                </Slide>
              </template>
              <template v-if="showNavigation" #addons>
                  <Navigation>
                    <template #next>
                      <img
                        src="assets/icons/slide-right.svg"
                        alt="Next"
                        class="w-6 h-6"
                      />
                    </template>
                    <template #prev>
                      <img
                        src="assets/icons/slide-left.svg"
                        alt="Previous"
                        class="w-6 h-6"
                      />
                    </template>
                  </Navigation>
              </template>
            </Carousel>
          </div>
          <div class="text-red-500" v-else>
            Fehlkonfiguration im Backend. Es wurden keine Produkte gewählt.
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<style scoped>
.carousel :deep(.carousel__prev),
.carousel :deep(.carousel__next) {
  @apply bg-skin-icon text-skin-white w-12 h-12 rounded-full flex items-center justify-center mr-0;
  pointer-events: all;
}


.carousel :deep(.carousel__prev){
  @apply absolute top-1/4 left-[10px] lg:left-1/2 lg:-translate-x-[290px];
}

.carousel :deep(.carousel__next){
  @apply absolute top-1/4 right-[10px];
}

.carousel :deep(.carousel__viewport) {
  overflow-y: hidden;
  overflow-x: hidden;
}

@media (min-width: 768px) {
  .carousel :deep(.carousel__viewport) {
    overflow-y: visible;
    overflow-x: visible;
  }
}

</style>